import type { IOfferRow } from '../types';

/**
 * Default columns for the coordinator table (vo_v_offer_coordinator fields).
 * The actual column order comes from ref_v_offer_column at runtime.
 */

export type OfferRowField = keyof IOfferRow;

export const COORDINATOR_FIELDS: OfferRowField[] = [
    'num_row', 'status_name', 'ware_category_name', 'season_name',
    'quantity', 'purchase_price', 'vendor_selling_price', 'wholesale_price',
    'vendor_nds', 'vendor_ware', 'vendore_code', 'vendore_color_code',
    'vendor_color', 'vendor_size', 'vendor_description', 'vendor_brand',
    'vendor_country_manufactory', 'vendor_gendor', 'vendor_season',
    'vendor_composition', 'vendor_age_group', 'box_num', 'barcode',
    'date_expiration', 'date_production', 'foto_url',
];

export const NUMBER_FIELDS: OfferRowField[] = [
    'quantity', 'purchase_price', 'vendor_selling_price', 'wholesale_price', 'vendor_nds',
];

export const FIELD_LABELS: Record<OfferRowField, string> = {
    code: 'Код',
    offer_code: 'Код предложения',
    num_row: '№ строки',
    status_row: 'Код статуса',
    status_name: 'Статус',
    ware_group_code: 'Код ТГ',
    ware_category_name: 'ТГ',
    season_code: 'Код сезона',
    season_name: 'Сезон',
    quantity: 'Количество',
    purchase_price: 'Закупочная цена',
    vendor_selling_price: 'Цена продажи пост.',
    wholesale_price: 'Оптовая цена',
    vendor_nds: 'НДС пост.',
    foto_url: 'Фото',
    vendor_ware: 'Артикул',
    vendore_code: 'Код пост.',
    vendore_color_code: 'Код цвета',
    vendor_color: 'Цвет',
    vendor_size: 'Размер',
    vendor_description: 'Описание',
    vendor_brand: 'Бренд',
    vendor_country_manufactory: 'Страна произв.',
    vendor_gendor: 'Пол',
    vendor_season: 'Сезон пост.',
    vendor_composition: 'Состав',
    vendor_age_group: 'Возрастная гр.',
    box_num: '№ коробки',
    barcode: 'Штрихкод',
    date_expiration: 'Срок годности',
    date_production: 'Дата произв.',
};

export const INITIAL_WIDTHS: Partial<Record<OfferRowField, number>> = {
    num_row: 60,
    status_name: 120,
    ware_category_name: 120,
    season_name: 100,
    quantity: 80,
    purchase_price: 100,
    vendor_selling_price: 100,
    wholesale_price: 100,
    vendor_nds: 80,
    vendor_ware: 120,
    vendore_code: 100,
    vendore_color_code: 100,
    vendor_color: 100,
    vendor_size: 80,
    vendor_description: 200,
    vendor_brand: 120,
    vendor_country_manufactory: 120,
    vendor_gendor: 80,
    vendor_season: 100,
    vendor_composition: 150,
    vendor_age_group: 100,
    box_num: 80,
    barcode: 120,
    date_expiration: 100,
    date_production: 100,
    foto_url: 80,
};

// ── Buyer selection table columns ─────────────────────────
// Coordinator fields (read-only from sel.coordinator) + selection editable fields (sel_* prefix)

export type SelectionField =
    // Coordinator read-only
    | 'num_row' | 'coord_status' | 'ware_group' | 'season_name'
    | 'coord_quantity' | 'coord_purchase_price' | 'vendor_selling_price' | 'wholesale_price'
    | 'vendor_nds' | 'vendor_ware' | 'vendore_code' | 'vendore_color_code'
    | 'vendor_color' | 'vendor_size' | 'vendor_description' | 'vendor_brand'
    | 'vendor_country_manufactory' | 'vendor_gendor' | 'vendor_season'
    | 'vendor_composition' | 'vendor_age_group' | 'box_num' | 'barcode'
    | 'date_expiration' | 'date_production' | 'foto_url'
    // Selection editable
    | 'sel_quantity' | 'sel_purchase_price' | 'sel_selling_price' | 'sel_recommended_price'
    | 'sel_status'
    // Meta
    | 'history';

export const SELECTION_FIELDS: SelectionField[] = [
    'num_row', 'coord_status', 'ware_group', 'season_name',
    'coord_quantity', 'coord_purchase_price', 'vendor_selling_price', 'wholesale_price',
    'vendor_nds', 'vendor_ware', 'vendore_code', 'vendore_color_code',
    'vendor_color', 'vendor_size', 'vendor_description', 'vendor_brand',
    'vendor_country_manufactory', 'vendor_gendor', 'vendor_season',
    'vendor_composition', 'vendor_age_group', 'box_num', 'barcode',
    'date_expiration', 'date_production', 'foto_url',
    'sel_quantity', 'sel_purchase_price', 'sel_selling_price', 'sel_recommended_price',
    'sel_status', 'history',
];

export const SELECTION_LABELS: Record<SelectionField, string> = {
    num_row: '№ строки',
    coord_status: 'Статус (коорд.)',
    ware_group: 'ТГ',
    season_name: 'Сезон',
    coord_quantity: 'Кол-во',
    coord_purchase_price: 'Закуп. цена',
    vendor_selling_price: 'Цена продажи пост.',
    wholesale_price: 'Оптовая цена',
    vendor_nds: 'НДС пост.',
    vendor_ware: 'Артикул',
    vendore_code: 'Код пост.',
    vendore_color_code: 'Код цвета',
    vendor_color: 'Цвет',
    vendor_size: 'Размер',
    vendor_description: 'Описание',
    vendor_brand: 'Бренд',
    vendor_country_manufactory: 'Страна произв.',
    vendor_gendor: 'Пол',
    vendor_season: 'Сезон пост.',
    vendor_composition: 'Состав',
    vendor_age_group: 'Возрастная гр.',
    box_num: '№ коробки',
    barcode: 'Штрихкод',
    date_expiration: 'Срок годности',
    date_production: 'Дата произв.',
    foto_url: 'Фото',
    sel_quantity: 'Кол-во для заказа',
    sel_purchase_price: 'ЗЦ (отборка)',
    sel_selling_price: 'РЦ (отборка)',
    sel_recommended_price: 'РРЦ',
    sel_status: 'Статус отборки',
    history: 'Истор.',
};

export const SELECTION_INITIAL_WIDTHS: Record<SelectionField, number> = {
    num_row: 60,
    coord_status: 120,
    ware_group: 130,
    season_name: 100,
    coord_quantity: 80,
    coord_purchase_price: 100,
    vendor_selling_price: 100,
    wholesale_price: 100,
    vendor_nds: 80,
    vendor_ware: 120,
    vendore_code: 100,
    vendore_color_code: 100,
    vendor_color: 100,
    vendor_size: 80,
    vendor_description: 200,
    vendor_brand: 120,
    vendor_country_manufactory: 120,
    vendor_gendor: 80,
    vendor_season: 100,
    vendor_composition: 150,
    vendor_age_group: 100,
    box_num: 80,
    barcode: 120,
    date_expiration: 100,
    date_production: 100,
    foto_url: 80,
    sel_quantity: 110,
    sel_purchase_price: 120,
    sel_selling_price: 120,
    sel_recommended_price: 100,
    sel_status: 120,
    history: 60,
};

export const SELECTION_NUMBER_FIELDS: SelectionField[] = [
    'num_row', 'coord_quantity', 'coord_purchase_price', 'vendor_selling_price',
    'wholesale_price', 'vendor_nds',
    'sel_quantity', 'sel_purchase_price', 'sel_selling_price', 'sel_recommended_price',
];

// Legacy export for backward compat
export const BUYER_SELECTION_LABELS = SELECTION_LABELS;
